import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { CourseModuleContent } from '../../types'
import { Alert, Button, Card, CardBody, Skeleton } from '../../components/ui'
import { CheckIcon } from '../../components/icons'

interface ModuleResponse {
  ok: boolean
  module: CourseModuleContent
  module_index: number
  total_modules: number
  prev_index: number | null
  next_index: number | null
  is_last: boolean
  completed: boolean
  has_section_assessment: boolean
  section_assess_passed: boolean
}

export default function ModuleViewer() {
  const { courseId, moduleIndex } = useParams<{ courseId: string; moduleIndex: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['learning', 'module', courseId, moduleIndex],
    queryFn: () => api.get<ModuleResponse>(`/learning/courses/${courseId}/modules/${moduleIndex}`),
    enabled: Boolean(courseId) && moduleIndex !== undefined,
  })

  const complete = useMutation({
    mutationFn: () => api.post<{ ok: boolean; has_assessment: boolean; next_module: number | null }>(
      `/learning/courses/${courseId}/modules/${moduleIndex}/complete`,
    ),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['learning'] })
      if (res.has_assessment) {
        navigate(`/learning/${courseId}/modules/${moduleIndex}/assessment`)
      } else if (res.next_module !== null) {
        navigate(`/learning/${courseId}/modules/${res.next_module}`)
      } else {
        navigate(`/learning/${courseId}`)
      }
    },
  })

  if (isError) {
    return <Alert tone="danger">{(error as Error).message}</Alert>
  }
  if (isLoading || !data) {
    return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={200} /></div>
  }

  const { module, module_index, total_modules, completed, has_section_assessment, section_assess_passed } = data

  return (
    <>
      <div className="page-head">
        <div>
          <p className="page-head__sub" style={{ marginBottom: 4 }}>
            Module {module_index + 1} of {total_modules}
          </p>
          <h1>{module.module_name}</h1>
        </div>
      </div>

      <Card>
        <CardBody>
          <div className="stack" style={{ gap: 'var(--sp-4)' }}>
            {module.module_description && <p style={{ margin: 0 }}>{module.module_description}</p>}

            {module.content && (
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{module.content}</div>
            )}

            {module.key_topics && module.key_topics.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Key topics</div>
                <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                  {module.key_topics.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            )}

            {module.regulations_covered && module.regulations_covered.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Regulations covered</div>
                <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                  {module.regulations_covered.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            )}

            {module.module_media && module.module_media.length > 0 && (
              <div className="stack">
                {module.module_media.map((m, i) => (
                  m.type === 'image' && m.url ? (
                    <figure key={i} style={{ margin: 0 }}>
                      <img src={m.url} alt={m.caption || ''} style={{ maxWidth: '100%', borderRadius: 'var(--radius)' }} />
                      {m.caption && <figcaption className="page-head__sub">{m.caption}</figcaption>}
                    </figure>
                  ) : null
                ))}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {complete.isError && (
        <div style={{ marginTop: 'var(--sp-4)' }}>
          <Alert tone="danger">{(complete.error as Error).message}</Alert>
        </div>
      )}

      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 'var(--sp-6)' }}>
        {completed && !has_section_assessment ? (
          <Button icon={<CheckIcon />} disabled>
            Completed
          </Button>
        ) : has_section_assessment && section_assess_passed ? (
          <Button icon={<CheckIcon />} disabled>
            Section assessment passed
          </Button>
        ) : (
          <Button loading={complete.isPending} onClick={() => complete.mutate()}>
            {has_section_assessment ? 'Continue to section assessment' : 'Mark complete & continue'}
          </Button>
        )}
      </div>
    </>
  )
}
